import { useContext, useEffect, useState } from 'react'
import { AuthContext } from 'contexts/AuthContext'
import { Wrapper } from 'components'
import {
  Button,
  Card,
  Input,
  Label,
  Select,
  SelectAction,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import {
  ArrowDown,
  ArrowsDownUp,
  ArrowUp,
  MagnifyingGlass,
} from 'phosphor-react'
import { renderClaimsBadge } from 'utils/renderClaimBadge'
import {
  ClaimListItem,
  GetClaimsParams,
  GetClaimsResponse,
} from 'api/types/claims'
import { claimsService } from 'api/services/claims'
import { useNavigate } from 'react-router-dom'
import { RouteNamesAuth } from 'routes/enum'

type SortProp = 'PolicyNo' | 'ClaimNo' | 'PolicyHolder' | 'Status'

export const Claims = () => {
  const { currentBroker } = useContext(AuthContext)
  const [tableData, setTableData] = useState<ClaimListItem[] | null>(null)
  const [sortProp, setSortProp] = useState<SortProp | null>(null)
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC')
  const brokerNo = currentBroker?.BrokerNo ? currentBroker?.BrokerNo : 0
  const navigate = useNavigate()

  const [queryVars, setQueryVars] = useState<GetClaimsParams>({
    brokerNo: brokerNo,
    claimNo: undefined,
    policyNo: undefined,
    status: undefined,
  })

  const fetchClaims = async () => {
    if (brokerNo === 0) return false

    try {
      const response = (await claimsService.getClaims({
        ...queryVars,
      })) as GetClaimsResponse

      setTableData(response.Claims as ClaimListItem[])
    } catch (error) {
      toast.error('Erro ao obter lista de sinistros')
      // setTableData(null)
    }
  }

  useEffect(() => {
    if (currentBroker?.IsGroup) {
      navigate(RouteNamesAuth.DASHBOARD, { replace: true })
    }
  }, [currentBroker])

  useEffect(() => {
    fetchClaims()
  }, [brokerNo])

  const handleSort = (prop: SortProp) => {
    setSortProp(prev => {
      if (prev === prop) {
        if (sortOrder === 'ASC') {
          setSortOrder('DESC')
        } else {
          setSortOrder('ASC')
        }
      } else {
        setSortOrder('ASC')
      }
      return prop
    })
  }

  useEffect(() => {
    if (!tableData || !sortProp) return

    const sorted = [...tableData].sort((a, b) => {
      const aValue = a[sortProp]
      const bValue = b[sortProp]

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'ASC' ? aValue - bValue : bValue - aValue
      }
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'ASC'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }
      return 0
    })

    setTableData(sorted)
  }, [sortProp, sortOrder])

  return (
    <div className="w-full flex-1 py-12">
      <Wrapper>
        <h2 className="uppercase text-sm font-medium text-metal-900">
          Estado de Sinistros
        </h2>
        <h1 className="pb-10 text-xxxl font-medium text-primary-600">
          Consulte o estado dos seus sinistros
        </h1>

        <div className="py-8">
          <Card className="w-full max-w-full p-4 md:p-6">
            <div className="grid grid-col-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 w-full items-end">
              <div>
                <Label className="text-body-4 block pb-2">Nº Apólice</Label>
                <Input
                  placeholder="Nº Apólice"
                  value={queryVars.policyNo ?? ''}
                  type="text"
                  onChange={e => {
                    const val = e.target.value
                    setQueryVars(prev => ({
                      ...prev,
                      policyNo: val ? Number(val) : undefined,
                    }))
                  }}
                />
              </div>
              <div>
                <Label className="text-body-4 block pb-2">Nº Sinistro</Label>
                <Input
                  placeholder="Nº Sinistro"
                  value={queryVars.claimNo ?? ''}
                  type="text"
                  onChange={e => {
                    const val = e.target.value
                    setQueryVars(prev => ({
                      ...prev,
                      claimNo: val ? Number(val) : undefined,
                    }))
                  }}
                />
              </div>

              <div>
                <Label className="text-body-4 block pb-2">Estado</Label>
                <Select
                  onValueChange={val => {
                    if (val === 'none') {
                      setQueryVars(prev => ({ ...prev, status: undefined }))
                      return
                    }
                    setQueryVars(prev => ({ ...prev, status: val }))
                  }}
                >
                  <SelectAction className="[&_span]:truncate [&_span]:max-w-[90%]">
                    <SelectValue placeholder="Estado" />
                  </SelectAction>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="none">Todos</SelectItem>
                      {[
                        { value: 'PENDING ', label: 'Pendente' },
                        { value: 'APPROVED', label: 'Aprovada' },
                        { value: 'CANCEL  ', label: 'Declinado' },
                      ].map(e => (
                        <SelectItem key={e.value} value={e.value}>
                          {e.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <Button
                color="primary"
                variant="default"
                className="h-11 gap-2 text-body-5"
                onClick={fetchClaims}
              >
                <MagnifyingGlass size={20} />
                Pesquisar
              </Button>
            </div>
          </Card>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <div className="flex items-center gap-1">
                  Nº da Apólice
                  <Button
                    variant="link"
                    className={`${
                      sortProp === 'PolicyNo'
                        ? 'text-primary-600'
                        : 'text-metal-400'
                    }`}
                    onClick={() => handleSort('PolicyNo')}
                  >
                    {sortProp === 'PolicyNo' && sortOrder === 'ASC' && (
                      <ArrowUp size={16} />
                    )}
                    {sortProp === 'PolicyNo' && sortOrder === 'DESC' && (
                      <ArrowDown size={16} />
                    )}
                    {sortProp !== 'PolicyNo' && <ArrowsDownUp size={16} />}
                  </Button>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Nº Sinistro
                  <Button
                    variant="link"
                    className={`${
                      sortProp === 'ClaimNo' ? 'text-primary' : 'text-metal-400'
                    }`}
                    onClick={() => handleSort('ClaimNo')}
                  >
                    {sortProp === 'ClaimNo' && sortOrder === 'ASC' && (
                      <ArrowUp size={16} />
                    )}
                    {sortProp === 'ClaimNo' && sortOrder === 'DESC' && (
                      <ArrowDown size={16} />
                    )}
                    {sortProp !== 'ClaimNo' && <ArrowsDownUp size={16} />}
                  </Button>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Tomador
                  <Button
                    variant="link"
                    className={`${
                      sortProp === 'PolicyHolder'
                        ? 'text-primary'
                        : 'text-metal-400'
                    }`}
                    onClick={() => handleSort('PolicyHolder')}
                  >
                    {sortProp === 'PolicyHolder' && sortOrder === 'ASC' && (
                      <ArrowUp size={16} />
                    )}
                    {sortProp === 'PolicyHolder' && sortOrder === 'DESC' && (
                      <ArrowDown size={16} />
                    )}
                    {sortProp !== 'PolicyHolder' && <ArrowsDownUp size={16} />}
                  </Button>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Estado
                  <Button
                    variant="link"
                    className={`${
                      sortProp === 'Status' ? 'text-primary' : 'text-metal-400'
                    }`}
                    onClick={() => handleSort('Status')}
                  >
                    {sortProp === 'Status' && sortOrder === 'ASC' && (
                      <ArrowUp size={16} />
                    )}
                    {sortProp === 'Status' && sortOrder === 'DESC' && (
                      <ArrowDown size={16} />
                    )}
                    {sortProp !== 'Status' && <ArrowsDownUp size={16} />}
                  </Button>
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableData &&
              tableData.map((claim: ClaimListItem) => {
                return (
                  <TableRow key={claim.ClaimNo} className="w-full">
                    <TableCell>{claim.PolicyNo}</TableCell>
                    <TableCell>{claim.ClaimNo}</TableCell>
                    <TableCell>{claim.PolicyHolder}</TableCell>
                    <TableCell>{renderClaimsBadge(claim.Status)}</TableCell>
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </Wrapper>
    </div>
  )
}
